import fetchWithAuth from "@/api/secureFetch";

const SCORE_ENDPOINT = "/api/product-creation/score-field";
// The backend deliberately runs the rubric evaluation before vectorization;
// each provider call has a 30-second ceiling, so the browser must allow the
// full sequential operation (plus a small transport margin) to finish.
export const SCORE_TIMEOUT_MS = 65_000;

/** Discover fields the backend evaluates for input quality. */
export type ScorableField = "problem" | "solution" | "personas";

export interface ScoreDraftIdentity {
    draftId: number;
    draftVersion: number;
}

export interface FieldScoreResult {
    field: ScorableField;
    score: number;
    feedback: string;
    draft_id?: number;
    draft_version?: number;
    source_hash?: string;
    vectorized?: boolean;
}

export interface ScoreBand {
    label: string;
    color: string;
}

/** Rubric bands, mirroring the score ranges in the backend prompts. */
export function scoreBand(score: number): ScoreBand {
    if (score >= 90) return { label: "Excellent", color: "var(--np-success)" };
    if (score >= 75) return { label: "Good", color: "var(--np-brand)" };
    if (score >= 50) return { label: "Needs Improvement", color: "var(--np-warn)" };
    if (score >= 25) return { label: "Poor", color: "var(--error, #b42318)" };
    return { label: "Very Poor", color: "var(--error, #b42318)" };
}

/** Evaluate one Discover field. Creation mode includes a persisted draft
 * identity so the backend can load canonical input; refresh mode remains a
 * display-only evaluation of the supplied value. */
export async function scoreField(
    field: ScorableField,
    value: string,
    token: string,
    options: { signal?: AbortSignal; timeoutMs?: number; draft?: ScoreDraftIdentity } = {}
): Promise<FieldScoreResult> {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort();
    if (options.signal?.aborted) controller.abort();
    else options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
        timeoutId = globalThis.setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new Error("Evaluation timed out — try again."));
        }, options.timeoutMs ?? SCORE_TIMEOUT_MS);
    });

    try {
        const response = await Promise.race([
            fetchWithAuth(SCORE_ENDPOINT, token, {
                method: "POST",
                body: JSON.stringify({
                    field,
                    value,
                    ...(options.draft
                        ? { draft_id: options.draft.draftId, draft_version: options.draft.draftVersion }
                        : {})
                }),
                signal: controller.signal
            }),
            timeoutPromise
        ]);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const message = typeof body?.error === "string" ? body.error : body?.error?.message;
            throw new Error(message || `Evaluation failed (${response.status})`);
        }
        return response.json();
    } catch (error) {
        const abortedByInternalTimeout = controller.signal.aborted && !options.signal?.aborted;
        if (timedOut || abortedByInternalTimeout) throw new Error("Evaluation timed out — try again.");
        throw error;
    } finally {
        if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
        options.signal?.removeEventListener("abort", abortFromCaller);
    }
}
